import { isRecord } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { isUuid } from '@/lib/entity'
import { useLocalStorageState } from '@/lib/use-local-storage-state'

export type EntityRef = { id: string; name: string }

const RECENTS_KEY_PREFIX = 'vantage.recent-entities'
const RECENTS_LIMIT = 5

function isEntityRef(value: unknown): value is EntityRef {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    isUuid(value.id) &&
    typeof value.name === 'string'
  )
}

function parseRecents(raw: string): EntityRef[] | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return undefined
  }
  return Array.isArray(parsed) ? parsed.filter(isEntityRef) : undefined
}

// Named bag (not a tuple) per the repo's "3+ values → object" convention; the
// mutators use the functional-updater form so they reconcile against the live store.
export function useEntityRecents(): {
  recents: EntityRef[]
  remember: (entity: EntityRef) => void
  rename: (id: string, name: string) => void
} {
  const userId = useAuth().user?.userId
  const [recents, setRecents] = useLocalStorageState<EntityRef[]>(
    `${RECENTS_KEY_PREFIX}:${userId ?? 'anon'}`,
    parseRecents,
    JSON.stringify,
    [],
  )

  function remember(entity: EntityRef): void {
    if (!userId) return
    setRecents((prev) =>
      [entity, ...prev.filter((r) => r.id !== entity.id)].slice(
        0,
        RECENTS_LIMIT,
      ),
    )
  }

  // Update a cached entry's name in place after a rename — order is preserved,
  // and absent ids are a no-op (nothing to keep in sync).
  function rename(id: string, name: string): void {
    if (!userId) return
    setRecents((prev) =>
      prev.some((r) => r.id === id)
        ? prev.map((r) => (r.id === id ? { ...r, name } : r))
        : prev,
    )
  }

  return { recents, remember, rename }
}
